import type { Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { CanvasRenderingTarget2D } from 'fancy-canvas'

import {
    BarPrice,
    Coordinate,
    IChartApi,
    IPriceFormatter,
    ISeriesApi,
    ISeriesPrimitive,
    ISeriesPrimitivePaneRenderer,
    ISeriesPrimitivePaneView,
    SeriesAttachedParameter,
    SeriesPrimitivePaneViewZOrder,
    SeriesType,
    Time
} from 'lightweight-charts'

function formatOverlayNumber(value: number): string {
    const sign = value < 0 ? '-' : ''
    const abs = Math.abs(value)

    // Count digits in the integer part
    const integerPart = Math.floor(abs)
    const integerDigits = integerPart.toString().length

    // For values with 5+ digits, use K suffix
    if (integerDigits >= 5) {
        const inK = abs / 1000
        const integerPartInK = Math.floor(inK)
        const kIntegerDigits = integerPartInK.toString().length

        let formatted: string
        if (kIntegerDigits >= 3) {
            // 3+ digits: show integer part only (e.g., 124160 -> 124K)
            formatted = Math.round(inK).toString()
        } else if (kIntegerDigits === 2) {
            // 2 digits: show 1 decimal place (e.g., 12345 -> 12.3K)
            formatted = inK.toFixed(1)
            formatted = parseFloat(formatted).toString()
        } else {
            // 1 digit: show 2 decimal places (e.g., 9234 -> 9.23K)
            formatted = inK.toFixed(2)
            formatted = parseFloat(formatted).toString()
        }
        return sign + formatted + 'K'
    }

    // For values with 3-4 digits, show integer only
    if (integerDigits >= 3) {
        return sign + Math.round(abs).toString()
    }

    // For values with 1-2 digits, show with up to 2 decimal places
    const formatted = abs.toFixed(2)
    return sign + parseFloat(formatted).toString()
}

interface RendererData {
    priceFormatter: IPriceFormatter
    coordinateToPrice: (coordinate: number) => BarPrice | null
    priceToCoordinate: (price: number) => Coordinate | null
    options: OverlayPriceScaleOptions
}

interface Label {
    label: string
    y: number
}

const tickSpacing = 40
const horizontalPadding = 3
const verticalPadding = 2
const sideMargin = 10
const fontSize = 12
const radius = 4

/** Pixel width to leave clear on the time scale where overlay axis labels sit */
export const OVERLAY_PRICE_SCALE_TIMESCALE_RESERVE_PX =
    sideMargin + horizontalPadding * 2 + fontSize * 5

class OverlayPriceScaleRenderer {
    _data: RendererData | null = null
    update(data: RendererData) {
        this._data = data
    }

    draw(target: CanvasRenderingTarget2D) {
        target.useMediaCoordinateSpace((scope) => {
            if (!this._data) return
            const labels = this._calculatePriceScale(scope.mediaSize.height, this._data)
            const maxLabelLength = labels.reduce((answer: number, label: Label) => {
                return Math.max(answer, label.label.length)
            }, 0)
            const testLabelForWidth = ''.padEnd(maxLabelLength, '0')
            const ctx = scope.context
            const isLeft = this._data.options.side === 'left'
            ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'
            const testDimensions = ctx.measureText(testLabelForWidth)
            const width = testDimensions.width
            const x = isLeft ? sideMargin : scope.mediaSize.width - sideMargin - width
            const textX = x + horizontalPadding + Math.round(width / 2)
            labels.forEach((label) => {
                ctx.beginPath()
                const topY = label.y - fontSize / 2
                ctx.roundRect(x, topY, width + horizontalPadding * 2, fontSize + 2 * verticalPadding, radius)
                ctx.fillStyle = this._data!.options.backgroundColor
                ctx.fill()
                ctx.beginPath()
                ctx.fillStyle = this._data!.options.textColor
                ctx.fillText(label.label, textX, topY + verticalPadding, width)
            })
        })
    }

    _calculatePriceScale(height: number, data: RendererData) {
        const yPositions: number[] = []
        const halfTick = Math.round(tickSpacing / 4)
        let pos = halfTick
        while (pos <= height - halfTick) {
            yPositions.push(pos)
            pos += tickSpacing
        }

        return yPositions
            .map((y) => {
                const price = data.coordinateToPrice(y)
                if (price === null) return null
                const priceLabel = data.priceFormatter.format(price)
                return {
                    label: priceLabel,
                    y: y
                }
            })
            .filter((item: Label | null): item is Label => Boolean(item))
    }
}

class OverlayPriceScaleView implements ISeriesPrimitivePaneView {
    private readonly _renderer: OverlayPriceScaleRenderer

    constructor() {
        this._renderer = new OverlayPriceScaleRenderer()
    }

    zOrder(): SeriesPrimitivePaneViewZOrder {
        return 'top'
    }

    renderer(): ISeriesPrimitivePaneRenderer {
        return this._renderer
    }

    update(data: RendererData) {
        this._renderer.update(data)
    }
}

export interface OverlayPriceScaleOptions {
    textColor: string
    backgroundColor: string
    side: 'left' | 'right'
    formatPrice?: (value: number) => string
}

export function getOverlayPriceScaleStyleOptions(theme: Theme): Pick<OverlayPriceScaleOptions, 'textColor' | 'backgroundColor'> {
    return {
        textColor: theme.palette.text.secondary,
        backgroundColor: alpha(theme.palette.background.paper, 0.50)
    }
}

const defaultOptions: OverlayPriceScaleOptions = {
    textColor: 'rgb(0, 0, 0)',
    backgroundColor: 'transparent',
    side: 'left'
} as const

// No default options - they should be provided based on theme
export class OverlayPriceScale implements ISeriesPrimitive<Time> {
    _paneViews: OverlayPriceScaleView[]
    _chart: IChartApi | null = null
    _series: ISeriesApi<SeriesType> | null = null
    _requestUpdate?: () => void
    _options: OverlayPriceScaleOptions

    constructor(options: Partial<OverlayPriceScaleOptions> = {}) {
        const mergedOptions = {
            ...defaultOptions,
            ...options
        }
        this._options = mergedOptions
        this._paneViews = [new OverlayPriceScaleView()]
    }

    applyOptions(options: Partial<OverlayPriceScaleOptions>) {
        this._options = {
            ...this._options,
            ...options
        }
        if (this._requestUpdate) this._requestUpdate()
    }

    attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>) {
        this._chart = chart
        this._series = series
        this._requestUpdate = requestUpdate
    }

    detached() {
        this._chart = null
        this._series = null
    }

    updateAllViews() {
        if (!this._series || !this._chart) return

        const coordinateToPrice = (coordinate: number): BarPrice | null => this._series!.coordinateToPrice(coordinate)

        const priceToCoordinate = (price: number): Coordinate | null => this._series!.priceToCoordinate(price)

        const formatPrice = this._options.formatPrice ?? formatOverlayNumber
        const customFormatter: IPriceFormatter = {
            format: (price: BarPrice) => formatPrice(Number(price))
        }

        const data: RendererData = {
            coordinateToPrice,
            priceToCoordinate,
            priceFormatter: customFormatter,
            options: this._options
        }

        this._paneViews.forEach((pw) => pw.update(data))
    }

    paneViews() {
        return this._paneViews
    }
}
